(() => {
  // All pair forms share one cart. Serializing adds keeps their before/after checks separate.
  const refreshers = new Set();
  let cartBusy = false;
  let cartReviewRequired = false;
  const refreshAll = () => refreshers.forEach(refresh => refresh());

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        signal: controller.signal
      });
      let body = null;
      try { body = await response.json(); } catch (_) {}
      return { response, body };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function cartQuantities(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    const quantities = new Map();
    cart.items.forEach(item => {
      const id = String(item.variant_id ?? item.id ?? '');
      const quantity = Number(item.quantity);
      if (!/^\d+$/.test(id) || !Number.isInteger(quantity) || quantity < 0) {
        throw new Error('Invalid cart item');
      }
      quantities.set(id, (quantities.get(id) || 0) + quantity);
    });
    return quantities;
  }

  function compareCart(before, after, requested) {
    const ids = new Set([...before.keys(), ...after.keys(), ...requested.keys()]);
    let exact = true;
    let unchanged = true;
    ids.forEach(id => {
      const delta = (after.get(id) || 0) - (before.get(id) || 0);
      if (delta !== (requested.get(id) || 0)) exact = false;
      if (delta !== 0) unchanged = false;
    });
    return { exact, unchanged };
  }

  function init(section) {
    if (section.dataset.initialized) return;
    const config = section.querySelector('[data-combinations-config]');
    if (!config) return;
    let data;
    try { data = JSON.parse(config.textContent); } catch (_) { return; }
    section.dataset.initialized = 'true';
    const arabic = data.locale === 'ar';
    const fallback = {
      chooseColour: arabic ? 'اختاري اللون' : 'Choose colour',
      chooseSize: arabic ? 'اختاري المقاس' : 'Choose size',
      unavailable: arabic ? 'غير متاح حالياً' : 'Currently unavailable',
      otherColour: arabic ? 'اختاري لوناً آخر بنفس المقاس:' : 'Choose another colour in the same size:',
      noAlternative: arabic ? 'لا يتوفر لون آخر بنفس المقاس حالياً.' : 'No other colour is currently available in this size.',
      reviewCart: arabic ? 'راجعي الحقيبة' : 'Review your bag',
      uncertain: arabic ? 'تعذر تأكيد إضافة القطعتين. راجعي الحقيبة قبل المحاولة مرة أخرى.' : 'We could not confirm both pieces were added. Review your bag before trying again.',
      selectionSummary: arabic ? 'اختياراتك' : 'Your selection',
      chooseBoth: arabic ? 'اختاري خيارات كل قطعة.' : 'Choose each piece’s options.'
    };
    const label = key => data.labels?.[key] || data[key] || fallback[key] || '';
    const money = cents => new Intl.NumberFormat(arabic ? 'ar-EG' : 'en-EG', {
      style: 'currency', currency: data.currency || 'EGP'
    }).format(cents / 100);
    const readCart = async () => {
      const { response, body } = await request(data.cartUrl || `${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Cart read failed');
      return { body, quantities: cartQuantities(body) };
    };

    section.querySelectorAll('[data-sitr-combination]').forEach(root => {
      const form = root.querySelector('form');
      if (!form) return;
      const button = form.querySelector('button[type="submit"]');
      const status = form.querySelector('[data-combination-status]');
      const error = form.querySelector('[data-combination-error]');
      const cart = form.querySelector('[data-combination-cart]');
      const variants = [...form.querySelectorAll('select[data-combination-variant]')];
      if (!button || !status || !error || !cart || variants.length !== 2) return;
      const original = button.textContent;
      const cartLabel = cart.textContent;
      let busy = false;
      let completedSignature = '';
      const pieces = variants.map(select => {
        const wrapper = select.closest('[data-combination-piece]');
        const image = wrapper?.querySelector('[data-piece-image]');
        return {
          select,
          wrapper,
          colour: wrapper?.querySelector('select[data-piece-colour]'),
          size: wrapper?.querySelector('select[data-piece-size]'),
          image,
          initialImage: image ? {
            src: image.getAttribute('src'),
            srcset: image.getAttribute('srcset'),
            alt: image.alt,
            hidden: image.hidden
          } : null,
          choice: wrapper?.querySelector('[data-piece-choice]'),
          initialChoice: wrapper?.querySelector('[data-piece-choice]')?.textContent || '',
          enhancements: [],
          alternatives: wrapper?.querySelector('[data-combination-alternatives]') ||
            (select.nextElementSibling?.matches('[data-combination-alternatives]') ? select.nextElementSibling : null),
          title: wrapper?.dataset.pieceTitle || ''
        };
      });
      const signature = () => variants.map(select => select.value).join(':');
      const selectedVariant = select => select.value ? select.selectedOptions[0] : null;
      const available = option => !!option?.value && !option.disabled && option.dataset.available === 'true';
      const incomplete = () => variants.some(select => !available(selectedVariant(select)));
      const optionLabels = new WeakMap();
      pieces.forEach(piece => [piece.colour, piece.size].filter(Boolean).forEach(control => {
        [...control.options].forEach(option => optionLabels.set(option, option.textContent.trim()));
      }));
      const displayValue = control => control?.value ? optionLabels.get(control.selectedOptions[0]) || control.value : '';
      const enhanceControl = (piece, control) => {
        if (!control?.id) return;
        const type = control === piece.colour ? 'colour' : 'size';
        const originalLabel = piece.wrapper.querySelector(`label[for="${control.id}"]`);
        const group = document.createElement('fieldset');
        group.setAttribute('class', `sitr-option-group sitr-option-group--${type}`);
        group.setAttribute('data-piece-option-group', type);
        const legend = document.createElement('legend');
        legend.textContent = originalLabel?.textContent || label(type === 'colour' ? 'chooseColour' : 'chooseSize');
        const options = document.createElement('div');
        options.setAttribute('class', 'sitr-options');
        const radios = [...control.options].filter(option => option.value).map((option, index) => {
          const choice = document.createElement('label');
          choice.setAttribute('class', 'sitr-option');
          const input = document.createElement('input');
          input.setAttribute('type', 'radio');
          input.setAttribute('class', 'sitr-option__input');
          input.setAttribute('id', `${control.id}-option-${index}`);
          input.setAttribute('name', `${control.id}-choice`);
          input.value = option.value;
          input.required = control.required;
          input.checked = control.value === option.value;
          choice.setAttribute('for', input.id);
          choice.append(input);
          if (type === 'colour') {
            // Canonical swatch styling only; keep localized selection values unchanged.
            const swatchColour = ({ 'زيتي': 'Olive', 'كحلي داكن': 'Dark Navy' })[option.value] || option.value;
            choice.setAttribute('data-colour', swatchColour);
            const swatch = document.createElement('span');
            swatch.setAttribute('class', 'sitr-option__swatch');
            swatch.setAttribute('data-colour', swatchColour);
            swatch.setAttribute('aria-hidden', 'true');
            choice.append(swatch);
          }
          const text = document.createElement('span');
          text.setAttribute('class', 'sitr-option__label');
          text.textContent = option.textContent;
          choice.append(text);
          input.addEventListener('change', () => {
            if (!input.checked || cartBusy) return;
            control.value = input.value;
            control.dispatchEvent(new Event('change', { bubbles: true }));
          });
          options.append(choice);
          return { input, choice, text, option };
        });
        if (!radios.length) return;
        group.append(legend, options);
        control.insertAdjacentElement('beforebegin', group);
        // Only remove the native select from the interaction tree after its replacement exists.
        control.required = false;
        control.hidden = true;
        control.setAttribute('aria-hidden', 'true');
        control.setAttribute('tabindex', '-1');
        if (originalLabel) originalLabel.hidden = true;
        piece.enhancements.push({ control, group, radios });
      };
      const updateRadios = piece => piece.enhancements.forEach(enhancement => {
        enhancement.radios.forEach(({ input, choice, text, option }) => {
          input.checked = enhancement.control.value === option.value;
          input.disabled = cartBusy;
          choice.setAttribute('data-selected', String(input.checked));
          choice.setAttribute('data-available', option.dataset.available || 'true');
          text.textContent = optionLabels.get(option) || option.value;
          input.setAttribute('aria-label', option.textContent);
          choice.setAttribute('title', option.textContent);
        });
      });
      const updateOptionLabels = piece => {
        if (!piece.colour) return;
        [piece.colour, piece.size].filter(Boolean).forEach(control => {
          [...control.options].forEach(option => {
            if (!option.value) return;
            const isColour = control === piece.colour;
            const colour = isColour ? option.value : piece.colour.value;
            const size = isColour ? piece.size?.value : option.value;
            const compatible = [...piece.select.options].some(variant => available(variant) &&
              (!colour || variant.dataset.colour === colour) && (!size || variant.dataset.size === size));
            option.textContent = optionLabels.get(option) + (compatible ? '' : ` · ${label('unavailable')}`);
            // Unavailable combinations remain selectable so their explicit alternatives can be shown.
            option.disabled = false;
            option.dataset.available = String(compatible);
          });
        });
      };
      const pieceSelection = piece => {
        if (!piece.colour) return selectedVariant(piece.select)?.textContent.trim() || label('chooseColour');
        const colour = displayValue(piece.colour) || label('chooseColour');
        const size = piece.size ? displayValue(piece.size) || label('chooseSize') : '';
        return [colour, size].filter(Boolean).join(' / ');
      };
      const resolveVariant = piece => {
        if (!piece.colour) return;
        const candidates = [...piece.select.options].filter(option => option.value &&
          option.dataset.colour === piece.colour.value &&
          (!piece.size || option.dataset.size === piece.size.value));
        const complete = !!piece.colour.value && (!piece.size || !!piece.size.value);
        // Never choose the first available colour, size, or ambiguous variant for the shopper.
        piece.select.value = complete && candidates.length === 1 ? candidates[0].value : '';
      };
      const updateImage = piece => {
        if (!piece.image) return;
        const selected = selectedVariant(piece.select);
        const colour = piece.colour?.value || selected?.dataset.colour;
        const preview = selected || (colour ? [...piece.select.options].find(option =>
          option.value && option.dataset.colour === colour && option.dataset.image) : null);
        if (preview?.dataset.image) {
          piece.image.removeAttribute('srcset');
          if (piece.image.getAttribute('src') !== preview.dataset.image) piece.image.src = preview.dataset.image;
          const imageChoice = piece.colour ? [displayValue(piece.colour), displayValue(piece.size)].filter(Boolean).join(' / ') : preview.textContent.trim();
          piece.image.alt = [piece.title, imageChoice].filter(Boolean).join(': ');
          piece.image.hidden = false;
        } else {
          ['src', 'srcset'].forEach(attribute => {
            const value = piece.initialImage[attribute];
            if (value === null) piece.image.removeAttribute(attribute);
            else if (piece.image.getAttribute(attribute) !== value) piece.image.setAttribute(attribute, value);
          });
          piece.image.alt = piece.initialImage.alt;
          piece.image.hidden = piece.initialImage.hidden;
        }
      };
      const updateAlternatives = piece => {
        const host = piece.alternatives;
        if (!host) return;
        host.replaceChildren();
        host.hidden = true;
        const selected = selectedVariant(piece.select);
        const chosenColour = piece.colour?.value || selected?.dataset.colour;
        const chosenSize = piece.size?.value || selected?.dataset.size || '';
        const complete = piece.colour ? !!chosenColour && (!piece.size || !!chosenSize) : !!selected;
        if (!complete || available(selected)) return;
        host.hidden = false;
        const note = document.createElement('p');
        const seen = new Set();
        const alternatives = [...piece.select.options].filter(option => {
          const colour = option.dataset.colour;
          if (!available(option) || option.dataset.size !== chosenSize || colour === chosenColour || seen.has(colour)) return false;
          seen.add(colour);
          return true;
        });
        note.textContent = alternatives.length ? `${label('unavailable')}. ${label('otherColour')}` : `${label('unavailable')}. ${label('noAlternative')}`;
        host.append(note);
        alternatives.forEach(option => {
          const control = document.createElement('button');
          control.type = 'button';
          control.disabled = cartBusy || cartReviewRequired;
          control.textContent = piece.colour ?
            optionLabels.get([...piece.colour.options].find(colour => colour.value === option.dataset.colour)) || option.dataset.colour :
            option.textContent;
          control.addEventListener('click', () => {
            if (cartBusy || cartReviewRequired) return;
            const target = piece.colour || piece.select;
            target.value = piece.colour ? option.dataset.colour : option.value;
            target.dispatchEvent(new Event('change', { bubbles: true }));
            const selectedRadio = piece.enhancements.find(enhancement => enhancement.control === target)?.radios.find(({ input }) => input.value === target.value)?.input;
            (selectedRadio || target).focus();
          });
          host.append(control);
        });
      };
      const refresh = () => {
        // Remove detached forms when Shopify replaces a section.
        if (!section.isConnected) { refreshers.delete(refresh); return; }
        pieces.forEach(piece => {
          resolveVariant(piece);
          updateOptionLabels(piece);
          updateRadios(piece);
          updateImage(piece);
          updateAlternatives(piece);
          if (piece.choice) piece.choice.textContent = (piece.colour ? piece.colour.value : piece.select.value) ? pieceSelection(piece) : piece.initialChoice;
          [piece.select, piece.colour, piece.size].filter(Boolean).forEach(control => { control.disabled = cartBusy; });
        });
        const alreadyAdded = !!completedSignature && signature() === completedSignature;
        button.disabled = cartBusy || cartReviewRequired || incomplete() || alreadyAdded;
        button.textContent = busy ? data.adding : cartReviewRequired ? label('reviewCart') : alreadyAdded ? data.added : original;
        form.setAttribute('aria-busy', busy ? 'true' : 'false');
        if (cartReviewRequired) {
          status.textContent = '';
          error.textContent = label('uncertain');
          error.hidden = false;
          cart.textContent = label('reviewCart');
          cart.hidden = false;
        }
        const summary = root.querySelector('[data-combination-summary]');
        if (summary) {
          const selections = pieces.map(piece => [piece.title, pieceSelection(piece)].filter(Boolean).join(': ')).join(' + ');
          const template = label('selectionSummary');
          summary.textContent = variants.every(select => selectedVariant(select))
            ? (template.includes('__PIECES__') ? template.replace('__PIECES__', selections) : `${template}: ${selections}`)
            : label('chooseBoth');
        }
        const prices = variants.map(select => Number(selectedVariant(select)?.dataset.price ?? select.dataset.defaultPrice));
        if (prices.every(Number.isFinite)) {
          const regular = root.querySelector('[data-combination-price]');
          if (regular) regular.textContent = money(prices.reduce((sum, price) => sum + price, 0));
          const eligible = root.querySelector('[data-combination-offer-price]');
          if (eligible) eligible.textContent = money(prices.reduce((sum, price) => sum + price - Math.round(price * .05), 0));
          const saving = root.querySelector('[data-combination-saving]');
          if (saving) saving.textContent = money(prices.reduce((sum, price) => sum + Math.round(price * .05), 0));
        }
      };
      const onChange = () => {
        if (cartBusy) return;
        error.hidden = true;
        status.textContent = '';
        cart.hidden = true;
        cart.textContent = cartLabel;
        refresh();
      };
      pieces.forEach(piece => {
        const controls = piece.colour ? [piece.colour, piece.size].filter(Boolean) : [piece.select];
        controls.forEach(control => control.addEventListener('change', onChange));
        [piece.colour, piece.size].filter(Boolean).forEach(control => enhanceControl(piece, control));
      });
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (cartBusy || cartReviewRequired || incomplete() || signature() === completedSignature) return;
        busy = true;
        cartBusy = true;
        status.textContent = '';
        error.hidden = true;
        cart.hidden = true;
        const submittedSignature = signature();
        const items = variants.map(select => ({ id: Number(select.value), quantity: 1 }));
        const requested = new Map();
        items.forEach(item => requested.set(String(item.id), (requested.get(String(item.id)) || 0) + item.quantity));
        refreshAll();
        let before;
        try {
          before = await readCart();
        } catch (_) {
          // No add request has been sent, so retrying this read-only failure is safe.
          error.textContent = data.failed;
          error.hidden = false;
          busy = false;
          cartBusy = false;
          refreshAll();
          return;
        }
        let addResult = null;
        try {
          // Exactly one POST. Neither an HTTP error nor a timeout proves that nothing was added.
          addResult = await request(data.addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
        } catch (_) {}
        try {
          const after = await readCart();
          const result = compareCart(before.quantities, after.quantities, requested);
          if (result.exact) {
            completedSignature = submittedSignature;
            status.textContent = data.added;
            cart.hidden = false;
            document.dispatchEvent(new CustomEvent('sitr:cart-added', {
              detail: { items: after.body.items.filter(item => requested.has(String(item.variant_id ?? item.id))), inlineConfirmation: true }
            }));
          } else if (result.unchanged && addResult?.response.status === 422) {
            // A completed inventory rejection with an unchanged cart can be retried explicitly.
            error.textContent = data.failed;
            error.hidden = false;
          } else {
            cartReviewRequired = true;
          }
        } catch (_) {
          cartReviewRequired = true;
        } finally {
          busy = false;
          cartBusy = false;
          refreshAll();
        }
      });
      refreshers.add(refresh);
      refresh();
    });
  }

  function revealPairs(hash = location.hash, scroll = true) {
    const isPairCard = ['#sitr-top-pants', '#sitr-pants-cover', '#sitr-cover-hijab'].includes(hash);
    if (hash !== '#sitr-pairs' && !isPairCard) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    target.closest('details')?.setAttribute('open', '');
    if (hash === '#sitr-pairs') target.querySelector('[data-sitr-pair-options]')?.setAttribute('open', '');
    if (scroll && isPairCard) {
      window.requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'auto' }));
    }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    const target = new URL(link.href, location.href);
    if (target.origin === location.origin && target.pathname === location.pathname && target.search === location.search) {
      revealPairs(target.hash);
    }
  });
  window.addEventListener('hashchange', () => revealPairs());
  window.addEventListener('pageshow', () => { refreshAll(); revealPairs(); });
  document.querySelectorAll('.sitr-combinations').forEach(init);
  revealPairs();
  document.addEventListener('shopify:section:load', event => {
    if (event.target.matches?.('.sitr-combinations')) init(event.target);
    event.target.querySelectorAll('.sitr-combinations').forEach(init);
    revealPairs();
  });
})();

